import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import SecurityIcon from "@mui/icons-material/Security";
import ShieldIcon from "@mui/icons-material/Shield";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useDispatch, useSelector } from "react-redux";
import { changePassword } from "../../redux/apiRequest";
import { createAxios } from "../../createAxios";
import { getUserId } from "../../utils/authSession";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "var(--toeic-surface)",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

function Account() {
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const email = currentUser?.metadata.user.user_email;
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const userID = getUserId(currentUser);
  const dispatch = useDispatch();
  const axiosJWT = createAxios(currentUser);

  const [formData, setFormData] = useState({
    email,
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setError("");
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.currentPassword) {
      setError("Current password is required");
      return;
    }
    if (formData.newPassword !== formData.confirmNewPassword) {
      setError("New passwords do not match");
      return;
    }

    const result = await changePassword(
      accessToken,
      userID,
      {
        email: formData.email,
        password: formData.currentPassword,
        new_password: formData.newPassword,
      },
      dispatch,
      axiosJWT
    );

    setSnackbarMessage(
      result ? "Password changed successfully!" : "Failed to change password. Please try again."
    );
    setSnackbarSeverity(result ? "success" : "error");
    setSnackbarOpen(true);
  };

  const passwordFields = [
    {
      label: "Current Password",
      name: "currentPassword",
      show: showCurrentPassword,
      toggle: () => setShowCurrentPassword((value) => !value),
    },
    {
      label: "New Password",
      name: "newPassword",
      show: showNewPassword,
      toggle: () => setShowNewPassword((value) => !value),
    },
    {
      label: "Confirm New Password",
      name: "confirmNewPassword",
      show: showConfirmNewPassword,
      toggle: () => setShowConfirmNewPassword((value) => !value),
    },
  ];

  return (
    <Box sx={{ width: "100%", mt: 4, mb: 6 }}>
      <Box component="header">
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Box sx={{ width: 58, height: 58, borderRadius: "16px", display: "grid", placeItems: "center", bgcolor: "#fff7d6", color: "#b77900", flex: "0 0 auto" }}>
            <LockResetIcon sx={{ fontSize: 38 }} />
          </Box>
          <Box>
            <Chip label="Account security" size="small" sx={{ mb: 1.25, bgcolor: "#e8f9ed", color: "#1f9b45", fontWeight: 700 }} />
            <Typography component="h1" sx={{ color: "var(--toeic-ink-deep)", fontSize: { xs: 34, md: 52 }, lineHeight: { xs: 1.05, md: 1 }, fontWeight: 800 }}>
              <Box component="span" sx={{ position: "relative", display: "inline-block", "&::before": { content: '""', position: "absolute", left: "-0.12em", right: "-0.12em", bottom: "0.04em", height: "0.44em", bgcolor: "rgba(240, 184, 77, 0.26)", borderRadius: "0.45em 0.6em 0.4em 0.55em", transform: "rotate(-1deg)" } }}>
                <Box component="span" sx={{ position: "relative" }}>Change Password</Box>
              </Box>
            </Typography>
            <Typography sx={{ mt: 1.5, color: "var(--toeic-ink-soft)", fontSize: 17, fontWeight: 700, lineHeight: 1.65 }}>
              Update your password to keep your Lingualica account protected.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 4, mb: 4, display: "grid", gap: 4 }}>
        <Box aria-hidden sx={{ borderTop: "1.5px dotted var(--toeic-line)" }} />
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) 300px", gap: 3, alignItems: "start" }}>
        <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, borderRadius: "14px", border: 0, boxShadow: "var(--toeic-shadow-card)", bgcolor: "var(--toeic-surface)" }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "grid", gap: 2 }}>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                InputProps={{ readOnly: true }}
                sx={fieldSx}
              />
              {passwordFields.map((field) => (
                <TextField
                  key={field.name}
                  fullWidth
                  label={field.label}
                  variant="outlined"
                  type={field.show ? "text" : "password"}
                  name={field.name}
                  value={formData[field.name]}
                  onChange={handleInputChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={field.toggle} edge="end">
                          {field.show ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={!!error && field.name !== "newPassword"}
                  helperText={field.name === "confirmNewPassword" ? error : ""}
                  sx={fieldSx}
                />
              ))}
              <Button
                fullWidth
                variant="contained"
                type="submit"
                startIcon={<SecurityIcon />}
                sx={{ mt: 1, borderRadius: "14px", bgcolor: "#35c759", color: "#ffffff", fontWeight: 700, "&:hover": { bgcolor: "#2fb44f" } }}
              >
                Change Password
              </Button>
            </Box>
          </form>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, borderRadius: "14px", border: "1.5px dotted var(--toeic-line)", bgcolor: "var(--toeic-paper-grid)", boxShadow: "var(--toeic-shadow-card)" }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", display: "grid", placeItems: "center", bgcolor: "#edf7ff", color: "#2196f3", mb: 1.5 }}>
            <ShieldIcon />
          </Box>
          <Typography sx={{ color: "var(--toeic-ink-deep)", fontWeight: 700 }}>Password tips</Typography>
          <Typography sx={{ mt: 1, color: "var(--toeic-ink-soft)", lineHeight: 1.7, fontWeight: 700 }}>
            Use a password that is unique to this account. Longer passwords with mixed letters,
            numbers, and symbols are harder to guess.
          </Typography>
        </Paper>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%", borderRadius: "14px", fontWeight: 800, boxShadow: "var(--toeic-shadow-card)" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Account;
